import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { CalendarDays, Plus, Pencil, Trash2, Lightbulb, Instagram, Facebook, Hash, ChevronLeft, ChevronRight, Sparkles, Wand2, Loader2 } from 'lucide-react';

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const PLATFORMS = ['Instagram', 'Facebook', 'TikTok'];
const PRODUCTS = ['Poke Bowl Thai', 'Burger UFO', 'Bubble Tea', 'Mochi Waffle', 'Coffee Corner'];
const STATUTS = [
  { value: 'idee', label: 'Idée', color: 'bg-gray-100 text-gray-700' },
  { value: 'en_cours', label: 'En cours', color: 'bg-blue-100 text-blue-700' },
  { value: 'planifie', label: 'Planifié', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'publie', label: 'Publié', color: 'bg-green-100 text-green-700' },
];

const PLATFORM_ICONS = {
  'Instagram': <Instagram className="w-4 h-4 text-pink-500" />,
  'Facebook': <Facebook className="w-4 h-4 text-blue-600" />,
  'TikTok': <Hash className="w-4 h-4 text-gray-800" />,
};

const PRODUCT_COLORS = {
  'Poke Bowl Thai': 'bg-emerald-100 text-emerald-700',
  'Burger UFO': 'bg-orange-100 text-orange-700',
  'Bubble Tea': 'bg-purple-100 text-purple-700',
  'Mochi Waffle': 'bg-pink-100 text-pink-700',
  'Coffee Corner': 'bg-amber-100 text-amber-700',
};

function StatusBadge({ statut }) {
  const s = STATUTS.find(x => x.value === statut) || STATUTS[0];
  return <Badge className={`${s.color} text-xs`}>{s.label}</Badge>;
}

export function SocialCalendarPage() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');
  const [tab, setTab] = useState('calendar');
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [evRes, postRes] = await Promise.all([
        api.get('/social-calendar/events'),
        api.get('/social-calendar/posts'),
      ]);
      setEvents(evRes.data);
      setPosts(postRes.data);
    } catch { toast.error('Erreur chargement'); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calendar helpers
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7; // Monday = 0
  const prevMonth = () => { if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); } else setCurrentMonth(m => m - 1); };
  const nextMonth = () => { if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); } else setCurrentMonth(m => m + 1); };

  // Events for current month
  const monthEvents = events.filter(e => e.mois === currentMonth + 1);
  const getEventsForDay = (day) => monthEvents.filter(e => e.jour === day);
  const getPostsForDay = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return posts.filter(p => p.date === dateStr);
  };

  // Filtered posts for list view
  const filteredPosts = posts.filter(p => {
    if (filterPlatform !== 'all' && p.plateforme !== filterPlatform) return false;
    if (filterProduct !== 'all' && p.produit !== filterProduct) return false;
    return true;
  });

  const handleSavePost = async (postData) => {
    try {
      if (editPost) {
        await api.put(`/social-calendar/posts/${editPost.id}`, postData);
        toast.success('Post mis à jour');
      } else {
        await api.post('/social-calendar/posts', postData);
        toast.success('Post créé');
      }
      setDialogOpen(false);
      setEditPost(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/social-calendar/posts/${id}`); toast.success('Supprimé'); fetchData(); } catch { toast.error('Erreur'); }
  };

  const handleAddFromSuggestion = (event, suggestion) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(event.jour).padStart(2, '0')}`;
    setEditPost(null);
    setDialogOpen(true);
    setTimeout(() => {
      window.__prefillPost = {
        date: dateStr,
        plateforme: suggestion.plateforme,
        produit: suggestion.produit,
        contenu: suggestion.idee,
        hashtags: suggestion.hashtags,
        event: event.event,
        statut: 'idee',
      };
      window.dispatchEvent(new Event('prefillPost'));
    }, 100);
  };

  const handleGenerateMonth = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/social-calendar/generate-month', {
        month: currentMonth + 1,
        year: currentYear,
      });
      toast.success(res.data.message || `${res.data.generated} posts générés !`);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la génération';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const PostForm = () => {
    const [form, setForm] = useState(editPost || { date: '', plateforme: 'Instagram', produit: 'Poke Bowl Thai', contenu: '', hashtags: '', statut: 'idee', event: '' });

    useEffect(() => {
      const handler = () => {
        if (window.__prefillPost) { setForm(window.__prefillPost); window.__prefillPost = null; }
      };
      window.addEventListener('prefillPost', handler);
      return () => window.removeEventListener('prefillPost', handler);
    }, []);

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="post-date" />
          </div>
          <div>
            <Label>Événement</Label>
            <Input value={form.event} onChange={e => setForm({...form, event: e.target.value})} placeholder="Ex: Saint-Valentin" data-testid="post-event" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Plateforme</Label>
            <Select value={form.plateforme} onValueChange={v => setForm({...form, plateforme: v})}>
              <SelectTrigger data-testid="post-platform"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Produit</Label>
            <Select value={form.produit} onValueChange={v => setForm({...form, produit: v})}>
              <SelectTrigger data-testid="post-product"><SelectValue /></SelectTrigger>
              <SelectContent>{PRODUCTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Statut</Label>
            <Select value={form.statut} onValueChange={v => setForm({...form, statut: v})}>
              <SelectTrigger data-testid="post-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Contenu du post</Label>
          <Textarea value={form.contenu} onChange={e => setForm({...form, contenu: e.target.value})} placeholder="Votre idée de post..." rows={3} data-testid="post-content" />
        </div>
        <div>
          <Label>Hashtags</Label>
          <Input value={form.hashtags} onChange={e => setForm({...form, hashtags: e.target.value})} placeholder="#ChanaPoke #PokeBowl" data-testid="post-hashtags" />
        </div>
        <Button className="w-full gap-2" onClick={() => handleSavePost(form)} data-testid="save-post-btn">
          {editPost ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {editPost ? 'Modifier' : 'Créer le post'}
        </Button>
      </div>
    );
  };

  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Calendrier Social Media</h1>
          <p className="text-muted-foreground mt-1">Planifiez vos posts Instagram, Facebook et TikTok</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditPost(null); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="new-post-btn" onClick={() => setEditPost(null)}>
              <Plus className="w-4 h-4" /> Nouveau post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editPost ? 'Modifier le post' : 'Nouveau post'}</DialogTitle></DialogHeader>
            <PostForm />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="calendar" className="gap-2"><CalendarDays className="w-4 h-4" /> Calendrier</TabsTrigger>
          <TabsTrigger value="suggestions" className="gap-2"><Lightbulb className="w-4 h-4" /> Suggestions</TabsTrigger>
          <TabsTrigger value="list" className="gap-2"><Sparkles className="w-4 h-4" /> Mes Posts ({posts.length})</TabsTrigger>
        </TabsList>

        {/* ====== CALENDRIER ====== */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-5 h-5" /></Button>
                  <CardTitle className="text-lg">{MONTHS[currentMonth]} {currentYear}</CardTitle>
                  <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-5 h-5" /></Button>
                </div>
                <Button
                  onClick={handleGenerateMonth}
                  disabled={generating}
                  className="gap-2"
                  data-testid="generate-month-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {generating ? 'Génération en cours...' : `Générer ${MONTHS[currentMonth]}`}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => (
                  <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
                ))}
                {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`empty-${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const dayEvents = getEventsForDay(day);
                  const dayPosts = getPostsForDay(day);
                  return (
                    <div
                      key={day}
                      className={`min-h-[80px] p-1 border rounded-md text-xs ${isToday(day) ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'}`}
                      data-testid={`cal-day-${day}`}
                    >
                      <div className={`font-medium mb-1 ${isToday(day) ? 'text-primary' : ''}`}>{day}</div>
                      {dayEvents.map((ev, idx) => (
                        <div key={idx} className="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-[10px] truncate mb-0.5" title={ev.event}>
                          {ev.event}
                        </div>
                      ))}
                      {dayPosts.map(p => (
                        <div
                          key={p.id}
                          className={`${PRODUCT_COLORS[p.produit] || 'bg-gray-100 text-gray-700'} px-1 py-0.5 rounded text-[10px] truncate cursor-pointer mb-0.5`}
                          title={`${p.plateforme}: ${p.contenu}`}
                          onClick={() => { setEditPost(p); setDialogOpen(true); }}
                        >
                          {p.plateforme.substring(0, 2)} {p.produit.split(' ')[0]}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== SUGGESTIONS ====== */}
        <TabsContent value="suggestions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-5 h-5" /></Button>
                  <span className="text-lg font-semibold mx-3">{MONTHS[currentMonth]} {currentYear}</span>
                  <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-5 h-5" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {monthEvents.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Aucun événement ce mois-ci</p>
              ) : (
                <div className="space-y-6">
                  {monthEvents.map((ev, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-800">{ev.jour} {MONTHS[currentMonth]}</Badge>
                        <h3 className="font-semibold">{ev.event}</h3>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {ev.suggestions.map((sug, si) => (
                          <Card key={si} className="border shadow-sm">
                            <CardContent className="p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {PLATFORM_ICONS[sug.plateforme]}
                                  <span className="text-xs font-medium">{sug.plateforme}</span>
                                </div>
                                <Badge className={`text-[10px] ${PRODUCT_COLORS[sug.produit]}`}>{sug.produit}</Badge>
                              </div>
                              <p className="text-sm">{sug.idee}</p>
                              <p className="text-xs text-blue-500">{sug.hashtags}</p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full gap-1 text-xs"
                                onClick={() => handleAddFromSuggestion(ev, sug)}
                                data-testid={`add-suggestion-${idx}-${si}`}
                              >
                                <Plus className="w-3 h-3" /> Ajouter à mon calendrier
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== MES POSTS ====== */}
        <TabsContent value="list">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle>Mes Posts</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                    <SelectTrigger className="w-[150px]" data-testid="filter-platform"><SelectValue placeholder="Plateforme" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterProduct} onValueChange={setFilterProduct}>
                    <SelectTrigger className="w-[170px]" data-testid="filter-product"><SelectValue placeholder="Produit" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les produits</SelectItem>
                      {PRODUCTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Plateforme</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead>Contenu</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPosts.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun post. Créez-en un ou utilisez les suggestions !</TableCell></TableRow>
                  ) : filteredPosts.map(post => (
                    <TableRow key={post.id}>
                      <TableCell className="font-medium">{formatDate(post.date)}</TableCell>
                      <TableCell><span className="flex items-center gap-1">{PLATFORM_ICONS[post.plateforme]} {post.plateforme}</span></TableCell>
                      <TableCell><Badge className={`text-xs ${PRODUCT_COLORS[post.produit]}`}>{post.produit}</Badge></TableCell>
                      <TableCell className="max-w-[300px]">
                        <p className="text-sm truncate">{post.contenu}</p>
                        {post.hashtags && <p className="text-xs text-blue-500 truncate">{post.hashtags}</p>}
                      </TableCell>
                      <TableCell><StatusBadge statut={post.statut} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditPost(post); setDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(post.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
